import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { isEmpty, some } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { CompanyApi, CompanyResponseModel, InvitationsApi, UsersApi } from '@vi-iot/client';

import { GatewayInvitationsActions } from './gateway-invitations.actions';
import { GatewayInvitationsStore } from './gateway-invitations.store';

@Injectable()
export class GatewayInvitationsEffects {
  listGatewayInvitations: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewayInvitationsActions.listGatewayInvitations),
      switchMap((action) =>
        this.invitationsApi
          .listInvitations(
            {
              installationId: action.installationId,
              customerNumber: action.customerNumber,
              limit: action.limit,
              offset: action.offset,
              status: action.status,
            },
            'v1'
          )
          .pipe(
            tap((response) => {
              response.data
                // get not empty and unique inviter IDs only
                .reduce(
                  (acc, invitation) =>
                    !some(acc, (inv) => inv.inviterAccount === invitation.inviterAccount) &&
                    !isEmpty(invitation.inviterAccount)
                      ? acc.concat(invitation)
                      : acc,
                  []
                )
                .forEach((invitation) => {
                  this.gatewayInvitationStore
                    .selectInviterDetails(invitation.inviterAccount)
                    .pipe(take(1), filter(isEmpty))
                    .subscribe(() => {
                      // fetch inviter details only if not already in store
                      this.store.dispatch(
                        GatewayInvitationsActions.getInviterDetails({
                          accountId: invitation.inviterAccount,
                        })
                      );
                    });
                });

              response.data
                // get not empty and unique invitee IDs only
                .reduce(
                  (acc, invitation) =>
                    !some(acc, (inv) => inv.inviteeAccount === invitation.inviteeAccount) &&
                    !isEmpty(invitation.inviteeAccount)
                      ? acc.concat(invitation)
                      : acc,
                  []
                )
                .forEach((invitation) => {
                  this.gatewayInvitationStore
                    .selectInviteeDetails(invitation.inviteeAccount)
                    .pipe(take(1), filter(isEmpty))
                    .subscribe(() => {
                      // fetch invitee details only if not already in store
                      this.store.dispatch(
                        GatewayInvitationsActions.getInviteeDetails({
                          accountId: invitation.inviteeAccount,
                        })
                      );
                    });
                });

              response.data
                .reduce(
                  (acc, invitation) =>
                    !some(
                      acc,
                      (inv) => inv.inviterCustomerNumber === invitation.inviterCustomerNumber
                    ) && !isEmpty(invitation.inviterCustomerNumber)
                      ? acc.concat(invitation)
                      : acc,
                  []
                )
                .forEach((invitation) => {
                  this.gatewayInvitationStore
                    .selectInviterCompanyDetails(invitation.inviterCustomerNumber)
                    .pipe(take(1), filter(isEmpty))
                    .subscribe(() => {
                      this.store.dispatch(
                        GatewayInvitationsActions.getInviterCompanyDetails({
                          token: invitation.token,
                          companyId: invitation.inviterCustomerNumber,
                        })
                      );
                    });
                });

              response.data
                // do not fetch company details if customer number is missing (Family & Friends)
                .reduce(
                  (acc, invitation) =>
                    !some(acc, (inv) => inv.customerNumber === invitation.customerNumber) &&
                    !isEmpty(invitation.customerNumber)
                      ? acc.concat(invitation)
                      : acc,
                  []
                )
                .forEach((invitation) => {
                  this.gatewayInvitationStore
                    .selectInviteeCompanyDetails(invitation.customerNumber)
                    .pipe(take(1), filter(isEmpty))
                    .subscribe(() => {
                      this.store.dispatch(
                        GatewayInvitationsActions.getInviteeCompanyDetails({
                          token: invitation.token,
                          companyId: invitation.customerNumber,
                        })
                      );
                    });
                });
            }),
            map((response) =>
              GatewayInvitationsActions.listGatewayInvitationsSuccess({
                invitations: response.data,
                next: response.paging.next,
              })
            ),
            catchError(() => of(GatewayInvitationsActions.listGatewayInvitationsError()))
          )
      )
    );
  });

  getInviterDetails: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewayInvitationsActions.getInviterDetails),
      mergeMap((action) =>
        this.usersApi.getUser(action.accountId).pipe(
          map((response) =>
            GatewayInvitationsActions.getInviterDetailsSuccess({
              accountId: action.accountId,
              response,
            })
          ),
          catchError(() => of(GatewayInvitationsActions.getInviterDetailsError()))
        )
      )
    );
  });

  getInviteeDetails: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewayInvitationsActions.getInviteeDetails),
      mergeMap((action) =>
        this.usersApi.getUser(action.accountId).pipe(
          map((response) =>
            GatewayInvitationsActions.getInviteeDetailsSuccess({
              accountId: action.accountId,
              response,
            })
          ),
          catchError(() => of(GatewayInvitationsActions.getInviteeDetailsError()))
        )
      )
    );
  });

  getInviteeCompanyDetails: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewayInvitationsActions.getInviteeCompanyDetails),
      mergeMap((action) =>
        this.companyApi.getUserCompany(action.companyId).pipe(
          map((response: CompanyResponseModel) =>
            GatewayInvitationsActions.getInviteeCompanyDetailsSuccess({
              token: action.token,
              companyId: action.companyId,
              data: response,
            })
          ),
          catchError(() => of(GatewayInvitationsActions.getInviteeCompanyDetailsError()))
        )
      )
    );
  });

  getInviterCompanyDetails: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewayInvitationsActions.getInviterCompanyDetails),
      mergeMap((action) =>
        this.companyApi.getUserCompany(action.companyId).pipe(
          map((response: CompanyResponseModel) =>
            GatewayInvitationsActions.getInviterCompanyDetailsSuccess({
              token: action.token,
              companyId: action.companyId,
              data: response,
            })
          ),
          catchError(() => of(GatewayInvitationsActions.getInviterCompanyDetailsError()))
        )
      )
    );
  });

  filterByStatus: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewayInvitationsActions.filterByStatus),
      switchMap((action) => of(GatewayInvitationsActions.listGatewayInvitations({ ...action })))
    );
  });

  constructor(
    private invitationsApi: InvitationsApi,
    private usersApi: UsersApi,
    private companyApi: CompanyApi,
    private actions$: Actions,
    private gatewayInvitationStore: GatewayInvitationsStore,
    private store: Store
  ) {}
}
